/**
 * W4-MAP: Slay-Spire-style campaign node graph generator.
 *
 * 5 chapters, each 10 rows × 4 cols.
 * Row 0 : 4 starting nodes (player picks one)
 * Rows 1-8 : 3-4 nodes per row, edges from each node to 1-3 nodes one row up
 * Row 9 : single boss node — all paths converge
 *
 * Distribution (rows 0-8):
 *   normal 50% · elite 15% · rest 15% · event 12% · mystery 8%
 */

export type NodeKind = 'normal' | 'elite' | 'rest' | 'event' | 'mystery' | 'boss';

export interface CampaignNode {
  id: string;                          // 'ch1-r3-c2'
  kind: NodeKind;
  position: { row: number; col: number };
  edges: string[];                     // outgoing node ids (one row up)
}

export interface CampaignChapter {
  chapterIndex: 1 | 2 | 3 | 4 | 5;
  nodes: CampaignNode[];
  rngSeed: number;
}

// ---------------------------------------------------------------------------
// Minimal seeded LCG (same params as glibc — deterministic across envs)
// ---------------------------------------------------------------------------
class Rng {
  private state: number;

  constructor(seed: number) {
    // Force into 32-bit unsigned range
    this.state = (seed >>> 0) || 1;
  }

  /** Returns a float in [0, 1) */
  next(): number {
    // LCG: multiplier 1664525, addend 1013904223, modulus 2^32
    this.state = ((Math.imul(1664525, this.state) + 1013904223) >>> 0);
    return this.state / 0x100000000;
  }

  /** Returns an integer in [0, n) */
  int(n: number): number {
    return Math.floor(this.next() * n);
  }

  /** Pick a random element from an array */
  pick<T>(arr: T[]): T {
    return arr[this.int(arr.length)];
  }

  /** Shuffle array in place (Fisher-Yates) */
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }
}

// ---------------------------------------------------------------------------
// Kind weights (non-boss rows)
// ---------------------------------------------------------------------------
const KIND_POOL: NodeKind[] = [
  'normal', 'normal', 'normal', 'normal', 'normal', // 50%
  'elite',                                           // 15% — but we need 15/100 and 8/100 etc.
  'rest',
  'event', 'event',
  'mystery',
  // We'll use weighted random below instead of this array
];
void KIND_POOL; // keep lint quiet — we use the function below

function pickKind(rng: Rng): NodeKind {
  const r = rng.next();
  if (r < 0.50) return 'normal';
  if (r < 0.65) return 'elite';
  if (r < 0.80) return 'rest';
  if (r < 0.92) return 'event';
  return 'mystery';
}

// ---------------------------------------------------------------------------
// Main generator
// ---------------------------------------------------------------------------
export function generateChapter(
  chapterIndex: 1 | 2 | 3 | 4 | 5,
  seed: number,
): CampaignChapter {
  const rng = new Rng(seed);
  const ROWS = 10;

  // grid[row] = array of column indices occupied in that row
  const grid: number[][] = [];

  // Row 0: always 4 starting columns
  grid[0] = [0, 1, 2, 3];

  // Rows 1-8: 3 or 4 columns (random)
  for (let r = 1; r <= 8; r++) {
    const count = rng.int(2) === 0 ? 3 : 4; // 50/50 between 3 and 4
    // Pick `count` distinct columns from [0,1,2,3]
    const cols = rng.shuffle([0, 1, 2, 3]).slice(0, count).sort((a, b) => a - b);
    grid[r] = cols;
  }

  // Row 9: boss only — virtual column 0 (centred in render)
  grid[9] = [0];

  // Build node map
  const nodeMap: Map<string, CampaignNode> = new Map();

  function nodeId(chapter: number, row: number, col: number): string {
    return `ch${chapter}-r${row}-c${col}`;
  }

  // Create all nodes
  for (let r = 0; r < ROWS; r++) {
    for (const c of grid[r]) {
      const id = nodeId(chapterIndex, r, c);
      const kind: NodeKind = r === 9 ? 'boss' : pickKind(rng);
      nodeMap.set(id, {
        id,
        kind,
        position: { row: r, col: c },
        edges: [],
      });
    }
  }

  // Build edges: each node in row r gets edges to 1-3 nodes in row r+1
  for (let r = 0; r < ROWS - 1; r++) {
    const currentCols = grid[r];
    const nextCols = grid[r + 1];

    for (const c of currentCols) {
      const fromId = nodeId(chapterIndex, r, c);
      const fromNode = nodeMap.get(fromId)!;

      // Find "nearby" columns in next row (prefer columns closest to c)
      // Sort next cols by distance to c
      const sorted = [...nextCols].sort((a, b) => Math.abs(a - c) - Math.abs(b - c));

      // Pick 1-3 edges; always at least 1 (the closest), cap at 3
      const maxEdges = Math.min(3, sorted.length);
      const edgeCount = 1 + rng.int(maxEdges); // 1..maxEdges
      const targets = sorted.slice(0, edgeCount);

      for (const tc of targets) {
        const toId = nodeId(chapterIndex, r + 1, tc);
        if (!fromNode.edges.includes(toId)) {
          fromNode.edges.push(toId);
        }
      }
    }

    // Guarantee: every node in next row (r+1) is reachable from at least one node in row r
    for (const nc of nextCols) {
      const toId = nodeId(chapterIndex, r + 1, nc);
      const hasIncoming = currentCols.some((c) =>
        nodeMap.get(nodeId(chapterIndex, r, c))!.edges.includes(toId),
      );
      if (!hasIncoming) {
        // Link from closest current col
        const closestC = [...currentCols].sort((a, b) => Math.abs(a - nc) - Math.abs(b - nc))[0];
        const fromNode = nodeMap.get(nodeId(chapterIndex, r, closestC))!;
        fromNode.edges.push(toId);
      }
    }
  }

  const nodes = Array.from(nodeMap.values()).sort((a, b) => {
    if (a.position.row !== b.position.row) return a.position.row - b.position.row;
    return a.position.col - b.position.col;
  });

  return { chapterIndex, nodes, rngSeed: seed };
}

// ---------------------------------------------------------------------------
// Helpers for campaign-map UI
// ---------------------------------------------------------------------------

/** All nodes in a given row */
export function nodesInRow(chapter: CampaignChapter, row: number): CampaignNode[] {
  return chapter.nodes.filter((n) => n.position.row === row);
}

/** Available nodes: reachable from visited nodes and not yet visited */
export function availableNodes(
  chapter: CampaignChapter,
  visitedIds: string[],
): CampaignNode[] {
  const visitedSet = new Set(visitedIds);

  // If nothing visited yet, all row-0 nodes are available
  if (visitedSet.size === 0) {
    return nodesInRow(chapter, 0);
  }

  const available = new Set<string>();
  for (const vid of visitedIds) {
    const node = chapter.nodes.find((n) => n.id === vid);
    if (node) {
      for (const edgeId of node.edges) {
        if (!visitedSet.has(edgeId)) {
          available.add(edgeId);
        }
      }
    }
  }

  return chapter.nodes.filter((n) => available.has(n.id));
}

/** Check if boss node is reachable from every possible path starting at any row-0 node */
export function allPathsReachBoss(chapter: CampaignChapter): boolean {
  const nodeMap = new Map(chapter.nodes.map((n) => [n.id, n]));
  const bossNode = chapter.nodes.find((n) => n.kind === 'boss');
  if (!bossNode) return false;

  const startNodes = nodesInRow(chapter, 0);

  for (const start of startNodes) {
    if (!canReachBoss(start.id, bossNode.id, nodeMap)) return false;
  }
  return true;
}

function canReachBoss(
  fromId: string,
  bossId: string,
  nodeMap: Map<string, CampaignNode>,
): boolean {
  if (fromId === bossId) return true;
  const node = nodeMap.get(fromId);
  if (!node || node.edges.length === 0) return false;
  return node.edges.some((eid) => canReachBoss(eid, bossId, nodeMap));
}

/** Number of nodes per kind across all non-boss rows */
export function kindCounts(chapter: CampaignChapter): Record<NodeKind, number> {
  const counts: Record<NodeKind, number> = {
    normal: 0, elite: 0, rest: 0, event: 0, mystery: 0, boss: 0,
  };
  for (const n of chapter.nodes) {
    counts[n.kind]++;
  }
  return counts;
}
